import { enqueueCompanyInvestigation } from './companyInvestigation';
import { nextWatchCompanyId } from './watchTowerSelectors';

import type {
  WatchAssociation,
  WatchBuilding,
  WatchCompany,
  WatchFinancial,
  WatchIdentifier,
  WatchOrgNode,
  WatchProduct,
  WatchShareholding,
  WatchTowerMockState,
} from './types';

export type ImportSeverity = 'error' | 'warning';

export interface ImportIssue {
  path: string,
  row: number | null,
  field: string,
  message: string,
  severity: ImportSeverity,
}

export interface WatchCompanyImportFile {
  schemaVersion: string,
  exportedAt?: string,
  sourceWatchCompanyId?: number | null,
  companies: Record<string, unknown>[],
}

const COUNTRY_RE = /^[A-Za-z]{2}$/;
const KNOWN_SCHEMES = new Set(['lei', 'duns', 'tax_id', 'ticker', 'isin', 'other']);
const IDENTIFIER_MATCH_ORDER = ['lei', 'duns', 'tax_id'] as const;

export function hasImportErrors(issues: ImportIssue[]): boolean {
  return issues.some((item) => item.severity === 'error');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asNullableString(value: unknown): string | null {
  const text = asString(value);
  return text || null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => asString(item)).filter(Boolean);
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function makeIssue(
  path: string,
  row: number | null,
  field: string,
  message: string,
  severity: ImportSeverity = 'error',
): ImportIssue {
  return {
    path, row, field, message, severity,
  };
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function identifierOf(company: WatchCompany, scheme: string): string | null {
  const match = company.identifiers.find((item) => item.scheme.toLowerCase() === scheme);
  return match?.value || null;
}

export function matchExistingCompanies(
  state: WatchTowerMockState,
  imported: Record<string, unknown>,
): WatchCompany[] {
  const identifiers = Array.isArray(imported.identifiers) ? imported.identifiers : [];
  const byScheme = new Map<string, string>();
  identifiers.forEach((item) => {
    if (!isRecord(item)) {
      return;
    }
    const scheme = asString(item.scheme).toLowerCase();
    const value = asString(item.value);
    if (scheme && value) {
      byScheme.set(scheme, value);
    }
  });

  const uid = asString(imported.uid);
  const name = asString(imported.name).toLowerCase();
  const country = asString(imported.addressCountry).toUpperCase();

  const identifierHits = IDENTIFIER_MATCH_ORDER
    .map((scheme) => {
      const value = byScheme.get(scheme);
      if (!value) {
        return [];
      }
      return state.watchCompanies.filter((company) => identifierOf(company, scheme) === value);
    })
    .find((hits) => hits.length);
  if (identifierHits) {
    return identifierHits;
  }

  if (uid) {
    const hits = state.watchCompanies.filter((company) => company.uid === uid);
    if (hits.length) {
      return hits;
    }
  }

  if (name && country) {
    return state.watchCompanies.filter((company) => (
      company.name.toLowerCase() === name && company.addressCountry.toUpperCase() === country
    ));
  }

  return [];
}

export function validateWatchCompanyImport(
  raw: unknown,
  state: WatchTowerMockState,
  fileName = 'file.json',
): ImportIssue[] {
  const issues: ImportIssue[] = [];
  const lowerName = fileName.toLowerCase();
  if (!lowerName.endsWith('.json')) {
    issues.push(makeIssue('file', null, 'format', 'This mock slice accepts JSON only. Use a .json file.'));
    return issues;
  }

  if (!isRecord(raw)) {
    issues.push(makeIssue('root', null, 'file', 'Root must be a JSON object.'));
    return issues;
  }

  if (asString(raw.schemaVersion) !== '1') {
    issues.push(makeIssue('root', null, 'schemaVersion', 'schemaVersion must be "1".'));
  }

  if (!Array.isArray(raw.companies)) {
    issues.push(makeIssue('root', null, 'companies', 'companies must be an array.'));
    return issues;
  }

  if (!raw.companies.length) {
    issues.push(makeIssue('root', null, 'companies', 'Add at least one company.'));
    return issues;
  }

  const leiInFile = new Map<string, number>();
  const uidInFile = new Map<string, number>();

  raw.companies.forEach((entry, index) => {
    const row = index + 1;
    const path = `companies[${index}]`;
    if (!isRecord(entry)) {
      issues.push(makeIssue(path, row, 'company', 'Company must be an object.'));
      return;
    }

    const name = asString(entry.name);
    const uid = asString(entry.uid);
    const country = asString(entry.addressCountry);

    if (!name) {
      issues.push(makeIssue(path, row, 'name', 'Missing name.'));
    }
    if (!uid) {
      issues.push(makeIssue(path, row, 'uid', 'Missing uid.'));
    }
    if (!country) {
      issues.push(makeIssue(path, row, 'addressCountry', 'Missing addressCountry.'));
    } else if (!COUNTRY_RE.test(country)) {
      issues.push(makeIssue(path, row, 'addressCountry', 'addressCountry must be ISO 3166-1 alpha-2.'));
    }

    if (uid) {
      const previous = uidInFile.get(uid);
      if (previous) {
        issues.push(makeIssue(path, row, 'uid', `Duplicate uid "${uid}" (also row ${previous}).`));
      } else {
        uidInFile.set(uid, row);
      }
    }

    const website = asString(entry.website);
    if (website && !isHttpUrl(website)) {
      issues.push(makeIssue(path, row, 'website', 'Website is not a URL.', 'warning'));
    }

    const identifiers = Array.isArray(entry.identifiers) ? entry.identifiers : [];
    if (!identifiers.length) {
      issues.push(makeIssue(path, row, 'identifiers', 'No identifiers.', 'warning'));
    }

    identifiers.forEach((item, identifierIndex) => {
      if (!isRecord(item)) {
        issues.push(makeIssue(
          `${path}.identifiers[${identifierIndex}]`,
          row,
          'identifiers',
          'Identifier must be an object.',
        ));
        return;
      }
      const scheme = asString(item.scheme).toLowerCase();
      const value = asString(item.value);
      if (!scheme || !value) {
        issues.push(makeIssue(
          `${path}.identifiers[${identifierIndex}]`,
          row,
          'identifiers',
          'Identifier needs scheme and value.',
        ));
        return;
      }
      if (!KNOWN_SCHEMES.has(scheme)) {
        issues.push(makeIssue(`${path}.identifiers[${identifierIndex}]`, row, 'scheme', `Unknown scheme "${scheme}".`));
      }
      if (scheme === 'lei') {
        const previous = leiInFile.get(value);
        if (previous) {
          issues.push(makeIssue(path, row, 'identifiers', `Duplicate LEI "${value}" (also row ${previous}).`));
        } else {
          leiInFile.set(value, row);
        }
      }
    });

    const products = Array.isArray(entry.products) ? entry.products : [];
    products.forEach((product, productIndex) => {
      if (!isRecord(product) || !asString(product.name)) {
        issues.push(makeIssue(`${path}.products[${productIndex}]`, row, 'name', 'Product without name.'));
      }
    });

    const buildings = Array.isArray(entry.buildings) ? entry.buildings : [];
    buildings.forEach((building, buildingIndex) => {
      if (!isRecord(building) || !asString(building.name)) {
        issues.push(makeIssue(`${path}.buildings[${buildingIndex}]`, row, 'name', 'Building without name.'));
      }
    });

    const matches = matchExistingCompanies(state, entry);
    if (matches.length > 1) {
      issues.push(makeIssue(path, row, 'identifiers', 'Identifier hits two registry rows. Do not merge.'));
    }
  });

  return issues;
}

export function parseWatchCompanyImportJson(
  text: string,
  fileName: string,
  state: WatchTowerMockState,
): { issues: ImportIssue[], payload: WatchCompanyImportFile | null } {
  if (!fileName.toLowerCase().endsWith('.json')) {
    return {
      payload: null,
      issues: [makeIssue('file', null, 'format', 'This mock slice accepts JSON only. Use a .json file.')],
    };
  }

  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return {
      payload: null,
      issues: [makeIssue('file', null, 'json', 'File is not valid JSON.')],
    };
  }

  const issues = validateWatchCompanyImport(raw, state, fileName);
  if (!isRecord(raw) || !Array.isArray(raw.companies)) {
    return { issues, payload: null };
  }

  return {
    issues,
    payload: {
      schemaVersion: asString(raw.schemaVersion),
      exportedAt: asNullableString(raw.exportedAt) || undefined,
      sourceWatchCompanyId: asNumber(raw.sourceWatchCompanyId),
      companies: raw.companies.filter(isRecord),
    },
  };
}

function nextChildId(companies: WatchCompany[], pick: (company: WatchCompany) => { id: number }[]): number {
  const ids = companies.flatMap((company) => pick(company).map((item) => item.id));
  return ids.reduce((max, id) => Math.max(max, id), 0) + 1;
}

function mapIdentifiers(value: unknown): WatchIdentifier[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((item) => ({
    scheme: asString(item.scheme).toLowerCase(),
    value: asString(item.value),
  })).filter((item) => item.scheme && item.value);
}

function mapProduct(raw: Record<string, unknown>, id: number, existing?: WatchProduct): WatchProduct {
  const criticality = raw.criticalityRating;
  return {
    id,
    name: asString(raw.name) || existing?.name || '',
    code: asNullableString(raw.code) ?? existing?.code ?? null,
    uid: asString(raw.uid) || existing?.uid || `WT-P-${id}`,
    productCategory: asNullableString(raw.productCategory) ?? existing?.productCategory ?? null,
    productType: asNullableString(raw.productType) ?? existing?.productType ?? null,
    upstreamTier: asNullableString(raw.upstreamTier) ?? existing?.upstreamTier ?? null,
    dateOpened: asNullableString(raw.dateOpened) ?? existing?.dateOpened ?? null,
    dateClosed: asNullableString(raw.dateClosed) ?? existing?.dateClosed ?? null,
    volumeQty: asNumber(raw.volumeQty) ?? existing?.volumeQty ?? null,
    volumeQtyUnit: asNullableString(raw.volumeQtyUnit) ?? existing?.volumeQtyUnit ?? null,
    relianceOnFreeNaturalResources: Array.isArray(raw.relianceOnFreeNaturalResources)
      ? asStringArray(raw.relianceOnFreeNaturalResources)
      : existing?.relianceOnFreeNaturalResources || [],
    relianceOnFreeSocialResources: Array.isArray(raw.relianceOnFreeSocialResources)
      ? asStringArray(raw.relianceOnFreeSocialResources)
      : existing?.relianceOnFreeSocialResources || [],
    criticalityRating: typeof criticality === 'number' || typeof criticality === 'string'
      ? criticality
      : existing?.criticalityRating ?? null,
    circularityFromDownstreamWaste: asNumber(raw.circularityFromDownstreamWaste)
      ?? existing?.circularityFromDownstreamWaste ?? null,
    hsCode: asNullableString(raw.hsCode) ?? existing?.hsCode ?? null,
    description: asNullableString(raw.description) ?? existing?.description ?? null,
  };
}

function mapBuilding(raw: Record<string, unknown>, id: number, existing?: WatchBuilding): WatchBuilding {
  return {
    id,
    name: asString(raw.name) || existing?.name || '',
    uid: asString(raw.uid) || existing?.uid || `WT-B-${id}`,
    legalName: asNullableString(raw.legalName) ?? existing?.legalName ?? null,
    ownership: asNullableString(raw.ownership) ?? existing?.ownership ?? null,
    equityShareholding: asNumber(raw.equityShareholding) ?? existing?.equityShareholding ?? null,
    financialControl: asNumber(raw.financialControl) ?? existing?.financialControl ?? null,
    businessUnitType: asNullableString(raw.businessUnitType) ?? existing?.businessUnitType ?? null,
    assetCategory: Array.isArray(raw.assetCategory) ? asStringArray(raw.assetCategory) : existing?.assetCategory || [],
    ownedLeased: asNullableString(raw.ownedLeased) ?? existing?.ownedLeased ?? null,
    numberOfEmployees: asNumber(raw.numberOfEmployees) ?? existing?.numberOfEmployees ?? null,
    dateOpened: asNullableString(raw.dateOpened) ?? existing?.dateOpened ?? null,
    dateClosed: asNullableString(raw.dateClosed) ?? existing?.dateClosed ?? null,
    startDate: asNullableString(raw.startDate) ?? existing?.startDate ?? null,
    endDate: asNullableString(raw.endDate) ?? existing?.endDate ?? null,
    naceSection: asNullableString(raw.naceSection) ?? existing?.naceSection ?? null,
    naceCode: asNullableString(raw.naceCode) ?? existing?.naceCode ?? null,
    euTaxonomyCode: asNullableString(raw.euTaxonomyCode) ?? existing?.euTaxonomyCode ?? null,
    netBookAmount: asNumber(raw.netBookAmount) ?? existing?.netBookAmount ?? null,
    netBookCurrency: asNullableString(raw.netBookCurrency) ?? existing?.netBookCurrency ?? null,
    addressCountry: asNullableString(raw.addressCountry) ?? existing?.addressCountry ?? null,
    addressState: asNullableString(raw.addressState) ?? existing?.addressState ?? null,
    addressPostCode: asNullableString(raw.addressPostCode) ?? existing?.addressPostCode ?? null,
    addressCity: asNullableString(raw.addressCity) ?? existing?.addressCity ?? null,
    addressStreet: asNullableString(raw.addressStreet) ?? existing?.addressStreet ?? null,
    addressBuilding: asNullableString(raw.addressBuilding) ?? existing?.addressBuilding ?? null,
    addressCoordinates: asNullableString(raw.addressCoordinates) ?? existing?.addressCoordinates ?? null,
  };
}

function mapAssociation(
  raw: Record<string, unknown>,
  id: number,
  companies: WatchCompany[],
): WatchAssociation {
  const relatedName = asString(raw.relatedCompanyName);
  const related = relatedName
    ? companies.find((company) => company.name.toLowerCase() === relatedName.toLowerCase())
    : null;
  return {
    id,
    eventKind: asString(raw.eventKind) || 'other',
    occurredOn: asString(raw.occurredOn),
    effectiveFrom: asNullableString(raw.effectiveFrom),
    effectiveTo: asNullableString(raw.effectiveTo),
    relatedWatchCompanyId: related?.id ?? asNumber(raw.relatedWatchCompanyId),
    personUid: asNullableString(raw.personUid),
    personFullName: asNullableString(raw.personFullName),
    personNationality: asNullableString(raw.personNationality),
    personDateOfBirth: asNullableString(raw.personDateOfBirth),
    personRole: asNullableString(raw.personRole),
    notes: asNullableString(raw.notes),
    payloadJson: isRecord(raw.payloadJson) ? raw.payloadJson : null,
  };
}

function mapFinancial(raw: Record<string, unknown>, id: number): WatchFinancial {
  const shareholding = Array.isArray(raw.shareholdingJson)
    ? raw.shareholdingJson.filter(isRecord).map((item) => ({
      holderName: asString(item.holderName),
      personUid: asNullableString(item.personUid),
      pct: asNumber(item.pct) || 0,
      kind: asString(item.kind) || 'other',
    })) as WatchShareholding[]
    : null;
  return {
    id,
    periodStart: asString(raw.periodStart),
    periodEnd: asString(raw.periodEnd),
    reportKind: asString(raw.reportKind) || 'other',
    currency: asNullableString(raw.currency),
    revenue: asNumber(raw.revenue),
    ebitda: asNumber(raw.ebitda),
    liquidity: asNumber(raw.liquidity),
    marketValuation: asNumber(raw.marketValuation),
    shareholdingJson: shareholding,
    sourceUrl: asNullableString(raw.sourceUrl),
    notes: asNullableString(raw.notes),
    payloadJson: isRecord(raw.payloadJson) ? raw.payloadJson : null,
  };
}

function mapOrgNodes(rawNodes: unknown, existing: WatchOrgNode[], nextIdStart: number): WatchOrgNode[] {
  if (!Array.isArray(rawNodes)) {
    return existing;
  }
  let nextId = nextIdStart;
  const keyToId = new Map<string, number>();
  const mapped: WatchOrgNode[] = [];
  rawNodes.filter(isRecord).forEach((node) => {
    const key = asString(node.key) || asString(node.name);
    const match = existing.find((item) => item.name === asString(node.name) && item.role === asString(node.role));
    const id = match?.id || nextId;
    if (!match) {
      nextId += 1;
    }
    if (key) {
      keyToId.set(key, id);
    }
    mapped.push({
      id,
      name: asString(node.name),
      role: asString(node.role) || 'legal_entity',
      parentId: null,
      ownershipPct: asNumber(node.ownershipPct),
    });
  });
  rawNodes.filter(isRecord).forEach((node, index) => {
    const parentKey = asString(node.parentKey);
    if (parentKey && mapped[index]) {
      mapped[index] = { ...mapped[index], parentId: keyToId.get(parentKey) ?? null };
    }
  });
  const incomingIds = new Set(mapped.map((node) => node.id));
  return [...existing.filter((node) => !incomingIds.has(node.id)), ...mapped];
}

function mergeBy<T extends { id: number }>(
  existing: T[],
  incoming: Record<string, unknown>[] | undefined,
  match: (current: T, raw: Record<string, unknown>) => boolean,
  mapItem: (raw: Record<string, unknown>, id: number, current?: T) => T,
  nextIdStart: number,
): T[] {
  if (!incoming) {
    return existing;
  }
  const result = [...existing];
  let nextId = nextIdStart;
  incoming.filter(isRecord).forEach((raw) => {
    const index = result.findIndex((item) => match(item, raw));
    if (index >= 0) {
      result[index] = mapItem(raw, result[index].id, result[index]);
      return;
    }
    result.push(mapItem(raw, nextId));
    nextId += 1;
  });
  return result;
}

function identityFromImport(raw: Record<string, unknown>, base: WatchCompany): WatchCompany {
  return {
    ...base,
    name: asString(raw.name) || base.name,
    code: raw.code === undefined ? base.code : asNullableString(raw.code),
    uid: asString(raw.uid) || base.uid,
    ownership: raw.ownership === undefined ? base.ownership : asNullableString(raw.ownership),
    dateOpened: raw.dateOpened === undefined ? base.dateOpened : asNullableString(raw.dateOpened),
    dateClosed: raw.dateClosed === undefined ? base.dateClosed : asNullableString(raw.dateClosed),
    naceSection: raw.naceSection === undefined ? base.naceSection : asNullableString(raw.naceSection),
    naceCode: raw.naceCode === undefined ? base.naceCode : asNullableString(raw.naceCode),
    euTaxonomyCode: raw.euTaxonomyCode === undefined ? base.euTaxonomyCode : asNullableString(raw.euTaxonomyCode),
    addressCountry: asString(raw.addressCountry).toUpperCase() || base.addressCountry,
    addressState: raw.addressState === undefined ? base.addressState : asNullableString(raw.addressState),
    addressCity: raw.addressCity === undefined ? base.addressCity : asNullableString(raw.addressCity),
    addressPostCode: raw.addressPostCode === undefined ? base.addressPostCode : asNullableString(raw.addressPostCode),
    addressStreet: raw.addressStreet === undefined ? base.addressStreet : asNullableString(raw.addressStreet),
    addressBuilding: raw.addressBuilding === undefined ? base.addressBuilding : asNullableString(raw.addressBuilding),
    addressCoordinates: raw.addressCoordinates === undefined
      ? base.addressCoordinates
      : asNullableString(raw.addressCoordinates),
    website: raw.website === undefined ? base.website : asNullableString(raw.website),
    notes: raw.notes === undefined ? base.notes : asNullableString(raw.notes),
    aliases: Array.isArray(raw.aliases) ? asStringArray(raw.aliases) : base.aliases,
    identifiers: Array.isArray(raw.identifiers) ? mapIdentifiers(raw.identifiers) : base.identifiers,
  };
}

export function applyWatchCompanyImport(
  state: WatchTowerMockState,
  payload: WatchCompanyImportFile,
): WatchTowerMockState {
  let companies = [...state.watchCompanies];
  let nextCompanyId = nextWatchCompanyId({ ...state, watchCompanies: companies });
  let nextProductId = nextChildId(companies, (company) => company.products);
  let nextBuildingId = nextChildId(companies, (company) => company.buildings);
  let nextOrgId = nextChildId(companies, (company) => company.orgNodes);
  let nextAssocId = nextChildId(companies, (company) => company.associations);
  let nextFinId = nextChildId(companies, (company) => company.financials);
  const upsertedIds: number[] = [];

  payload.companies.forEach((raw) => {
    const matches = matchExistingCompanies({ ...state, watchCompanies: companies }, raw);
    const base = matches[0] || {
      id: nextCompanyId,
      name: asString(raw.name),
      code: null,
      uid: asString(raw.uid),
      ownership: null,
      dateOpened: null,
      dateClosed: null,
      naceSection: null,
      naceCode: null,
      euTaxonomyCode: null,
      addressCountry: asString(raw.addressCountry).toUpperCase(),
      addressState: null,
      addressCity: null,
      addressPostCode: null,
      addressStreet: null,
      addressBuilding: null,
      addressCoordinates: null,
      website: null,
      status: 'active' as const,
      notes: null,
      identifiers: [],
      aliases: [],
      products: [],
      buildings: [],
      orgNodes: [],
      associations: [],
      financials: [],
      aiStatus: 'idle' as const,
      aiErrorMessage: null,
      aiSuggestion: null,
    };
    if (!matches[0]) {
      nextCompanyId += 1;
    }

    const withIdentity = identityFromImport(raw, base);
    const products = mergeBy(
      withIdentity.products,
      Array.isArray(raw.products) ? raw.products.filter(isRecord) : undefined,
      (current, item) => (
        (!!asString(item.code) && current.code === asString(item.code))
        || (!!asString(item.uid) && current.uid === asString(item.uid))
        || current.name === asString(item.name)
      ),
      (item, id) => {
        const mapped = mapProduct(item, id);
        if (id >= nextProductId) {
          nextProductId = id + 1;
        }
        return mapped;
      },
      nextProductId,
    );
    const buildings = mergeBy(
      withIdentity.buildings,
      Array.isArray(raw.buildings) ? raw.buildings.filter(isRecord) : undefined,
      (current, item) => (
        current.name === asString(item.name)
        || (!!asString(item.uid) && current.uid === asString(item.uid))
      ),
      (item, id) => {
        const mapped = mapBuilding(item, id);
        if (id >= nextBuildingId) {
          nextBuildingId = id + 1;
        }
        return mapped;
      },
      nextBuildingId,
    );
    const orgNodes = mapOrgNodes(raw.orgNodes, withIdentity.orgNodes, nextOrgId);
    nextOrgId = nextChildId([{ ...withIdentity, orgNodes } as WatchCompany], (company) => company.orgNodes);
    const associations = mergeBy(
      withIdentity.associations,
      Array.isArray(raw.associations) ? raw.associations.filter(isRecord) : undefined,
      (current, item) => (
        current.eventKind === asString(item.eventKind)
        && current.occurredOn === asString(item.occurredOn)
        && (current.personUid || '') === asString(item.personUid)
      ),
      (item, id) => {
        const mapped = mapAssociation(item, id, companies);
        if (id >= nextAssocId) {
          nextAssocId = id + 1;
        }
        return mapped;
      },
      nextAssocId,
    );
    const financials = mergeBy(
      withIdentity.financials,
      Array.isArray(raw.financials) ? raw.financials.filter(isRecord) : undefined,
      (current, item) => (
        current.periodStart === asString(item.periodStart)
        && current.periodEnd === asString(item.periodEnd)
        && current.reportKind === (asString(item.reportKind) || 'other')
      ),
      (item, id) => {
        const mapped = mapFinancial(item, id);
        if (id >= nextFinId) {
          nextFinId = id + 1;
        }
        return mapped;
      },
      nextFinId,
    );

    const nextCompany: WatchCompany = {
      ...withIdentity,
      products,
      buildings,
      orgNodes,
      associations,
      financials,
    };

    const existingIndex = companies.findIndex((company) => company.id === nextCompany.id);
    if (existingIndex >= 0) {
      companies[existingIndex] = nextCompany;
    } else {
      companies = [...companies, nextCompany];
    }
    upsertedIds.push(nextCompany.id);
  });

  return enqueueCompanyInvestigation({ ...state, watchCompanies: companies }, upsertedIds);
}
